<template>
	<section ref="elementRef" :class="containerClass">
		<div class="container px-4 mx-auto">
			<div class="mx-auto max-w-3xl">
				<!-- Header -->
				<div class="mb-12 text-center">
					<h2 class="mb-4 text-3xl font-bold text-gray-900">
						{{ title }}
					</h2>
					<p v-if="subtitle" class="text-lg text-gray-600">
						{{ subtitle }}
					</p>
				</div>

				<!-- Accordion Items -->
				<div class="space-y-4">
					<div
						v-for="(item, index) in items"
						:key="index"
						class="overflow-hidden rounded-lg border border-gray-200 transition-colors hover:border-blue-300"
						v-bind="itemAttributes?.(item, index)"
					>
						<!-- Header -->
						<button
							class="flex justify-between items-center px-6 py-4 w-full text-left bg-white transition-colors hover:bg-gray-50"
							@click="toggleItem(index)"
							:aria-expanded="expandedIndex === index"
							:aria-controls="`accordion-answer-${index}`"
							v-bind="headerAttributes?.(item, index)"
						>
							<span class="font-semibold text-gray-900" v-bind="questionAttributes?.(item, index)">
								{{ item.question }}
							</span>
							<!-- Icon slot with default chevron -->
							<slot name="icon" :is-expanded="expandedIndex === index" :index="index">
								<svg
									class="w-5 h-5 text-gray-600 transition-transform duration-300"
									:class="expandedIndex === index ? 'rotate-180' : ''"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M19 14l-7 7m0 0l-7-7m7 7V3"
									/>
								</svg>
							</slot>
						</button>

						<!-- Content -->
						<transition
							enter-active-class="transition-all duration-300"
							leave-active-class="transition-all duration-300"
							enter-from-class="max-h-0 opacity-0"
							enter-to-class="max-h-96 opacity-100"
							leave-from-class="max-h-96 opacity-100"
							leave-to-class="max-h-0 opacity-0"
						>
							<div
								v-if="expandedIndex === index"
								:id="`accordion-answer-${index}`"
								class="px-6 py-4 bg-gray-50 border-t border-gray-200"
								v-bind="answerAttributes?.(item, index)"
							>
								<!-- Content slot with default text rendering -->
								<slot name="content" :item="item" :index="index">
									<p class="leading-relaxed text-gray-700">
										{{ item.answer }}
									</p>
								</slot>

								<!-- Details list -->
								<div v-if="item.details" class="pt-4 mt-4 border-t border-gray-200">
									<ul class="space-y-2">
										<li
											v-for="detail in item.details"
											:key="detail"
											class="flex items-start text-sm text-gray-600"
										>
											<span
												class="flex-shrink-0 mt-2 mr-3 w-1.5 h-1.5 bg-blue-600 rounded-full"
											/>
											{{ detail }}
										</li>
									</ul>
								</div>
							</div>
						</transition>
					</div>
				</div>

				<!-- Bottom CTA -->
				<div v-if="showCta" class="mt-12 text-center">
					<p class="mb-4 text-gray-600">
						{{ ctaText }}
					</p>
					<button
						class="inline-flex items-center px-8 py-4 font-semibold text-white bg-blue-600 rounded-xl shadow-lg transition-colors hover:bg-blue-700"
						@click="handleCtaClick"
					>
						<slot name="cta-text">Jetzt starten</slot>
						<slot name="cta-icon">
							<svg class="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M13 7l5 5m0 0l-5 5m5-5H6"
								/>
							</svg>
						</slot>
					</button>
				</div>
			</div>
		</div>
	</section>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useComponentTracking } from '@/composables/useComponentTracking';

export interface AccordionItem {
	question: string;
	answer: string;
	details?: string[];
}

interface Props {
	title: string;
	subtitle?: string;
	items: AccordionItem[];
	backgroundColor?: string;
	showCta?: boolean;
	ctaText?: string;
	componentName?: string;
	sectionId?: string;
	sectionType?: string;
	eventPrefix?: string;
	// Functions to add custom attributes for schema.org or other purposes
	itemAttributes?: (item: AccordionItem, index: number) => Record<string, any>;
	headerAttributes?: (item: AccordionItem, index: number) => Record<string, any>;
	questionAttributes?: (item: AccordionItem, index: number) => Record<string, any>;
	answerAttributes?: (item: AccordionItem, index: number) => Record<string, any>;
}

const props = withDefaults(defineProps<Props>(), {
	backgroundColor: 'bg-white',
	showCta: false,
	ctaText: 'Haben Sie noch Fragen? Starten Sie jetzt den kostenlosen Test!',
	componentName: 'Accordion',
	sectionId: 'accordion',
	sectionType: 'interactive',
	eventPrefix: 'accordion',
});

const emit = defineEmits<{
	'cta-click': [];
	'item-opened': [index: number, item: AccordionItem];
	'item-closed': [index: number];
}>();

const { elementRef, trackEngagement, trackCtaClick } = useComponentTracking({
	componentName: props.componentName,
	sectionId: props.sectionId,
	sectionType: props.sectionType,
	trackOnVisible: true,
});

const expandedIndex = ref<number | null>(null);

const containerClass = computed(() => `py-16 ${props.backgroundColor}`);

const toggleItem = (index: number) => {
	const wasExpanded = expandedIndex.value === index;
	expandedIndex.value = wasExpanded ? null : index;

	if (!wasExpanded) {
		trackEngagement(`${props.eventPrefix}_opened`, {
			[`${props.eventPrefix}_index`]: index,
			[`${props.eventPrefix}_question`]: props.items[index]?.question,
		});
		emit('item-opened', index, props.items[index]);
	} else {
		trackEngagement(`${props.eventPrefix}_closed`, {
			[`${props.eventPrefix}_index`]: index,
		});
		emit('item-closed', index);
	}
};

function handleCtaClick() {
	trackCtaClick(props.ctaText, {
		item_count: props.items.length,
	});

	emit('cta-click');
}
</script>

<style scoped>
/* Smooth transitions for accordion */
.transition-all {
	transition-property: all;
	transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
	transition-duration: 300ms;
}
</style>
